import React, { useEffect, useState } from 'react'
import { Box, CssBaseline, Link, Stack, ThemeProvider, Typography, useMediaQuery } from '@mui/material'
import './App.css'
import Navigation from './components/Navigation'
import Dashboard from './components/Dashboard'
import { DarkTheme, LightTheme } from '@mercedes-benz/mui5-theme'
import Footer from './components/Footer'
import { Trans, useTranslation } from 'react-i18next'
import { setAlert } from './source/slice/AlertSlice'
import { useDispatch, useSelector } from 'react-redux'
import CustomSnackbar from './components/CustomSnackbar'
import axios, { AxiosRequestConfig } from 'axios'
import { IUser } from './interface/IUser'
import { setUser } from './source/slice/UserSlice'
import { useMsal } from '@azure/msal-react'
import { loginRequest } from './config/authConfig'
import NotAuthenticated from './components/NotAuthenticated'
import { format } from 'date-fns'
import { setTheme } from './source/slice/ThemeSlice'
import { InteractionRequiredAuthError, InteractionStatus, SilentRequest } from '@azure/msal-browser'
import { initializeApplicationInsights } from './config/applicationinsights'
import { setOrders } from './source/slice/OrderSlice'
import { IOrdersRef } from './interface/IOrder'
import Maintenance from './components/Maintenance'
import MailIcon from '@mui/icons-material/Mail'
import LaunchIcon from '@mui/icons-material/Launch'
import { injectedEndpoints } from './api/InjectedEndpoints'
import { store } from './source/Store'
import { setOperationalPeriods } from './source/slice/OperationalPeriodsSlice'
import { getAuthConfig } from './utils/authApi'
import QuickstartSidebar from './components/QuickstartSidebar'
import { useRefreshUser } from './utils/useRefreshUser'

export const isLocal = process.env.REACT_APP_LOCAL_DEVELOPMENT === 'true'

const QUICKSTART_SEEN_KEY = 'quickstartSeen'
const App = () => {
    const { t } = useTranslation()
    const { instance, accounts, inProgress } = useMsal()
    const isAuthenticated = accounts.length > 0
    const dispatch = useDispatch()
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
    const user: IUser = useSelector((state: any) => state.user?.value)
    const [userLoaded, setUserLoaded] = useState(false)

    const [quickstartOpen, setQuickstartOpen] = useState<boolean>(false)
    const { refreshUser } = useRefreshUser()

    useEffect(() => {
        if (!isAuthenticated) return
        if (inProgress !== InteractionStatus.None) return
        if (!accounts?.[0]) return

        let refreshTimer: ReturnType<typeof setTimeout> | undefined
        const REFRESH_INTERVAL_MS = 30 * 60_000

        const refreshToken = async (forceRefresh: boolean) => {
            try {
                const tokenResult = await instance.acquireTokenSilent({
                    account: accounts[0],
                    scopes: [scope],
                    forceRefresh,
                })

                if (tokenResult?.accessToken) {
                    const currentUser: IUser | undefined = (store.getState() as any).user?.value
                    dispatch(
                        setUser({
                            ...(currentUser ?? ({} as IUser)),
                            userid: currentUser?.userid ?? accounts[0]?.username,
                            token: tokenResult.accessToken,
                            worker: currentUser?.worker ?? null,
                            resource: currentUser?.resource ?? null,
                            isSeniorManager: currentUser?.isSeniorManager ?? false,
                            isPM: currentUser?.isPM ?? false,
                            isAdmin: currentUser?.isAdmin ?? false,
                        })
                    )
                }

                refreshTimer = setTimeout(() => refreshToken(false), REFRESH_INTERVAL_MS)
            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect({ account: accounts[0], scopes: [scope] })
                    return
                }
                refreshTimer = setTimeout(() => refreshToken(false), 5 * 60_000)
                console.error('refreshToken failed:', error)
            }
        }

        refreshToken(false)

        return () => {
            if (refreshTimer) {
                clearTimeout(refreshTimer)
            }
        }
    }, [isAuthenticated, inProgress, instance, accounts?.[0]?.homeAccountId, dispatch])

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return

        const initializeMsal = async () => {
            await instance.loginRedirect(loginRequest).catch((err: Error) => console.error('login failed', err))
        }

        if (!isAuthenticated) {
            initializeMsal()
        } else {
            if (!isLocal) {
                initializeApplicationInsights()
            }
            getCustomSnackbar()
            //setDefaultSelectedDate()
            getUser()
        }

        if (!localStorage.getItem('theme')) {
            // Default to following the OS theme (and persist the preference).
            dispatch(setTheme('system'))
        }
    }, [inProgress, instance, isAuthenticated])
    useEffect(() => {
        if (!isAuthenticated) return

        try {
            const seen = localStorage.getItem(QUICKSTART_SEEN_KEY)
            if (!seen) {
                setQuickstartOpen(true)
                localStorage.setItem(QUICKSTART_SEEN_KEY, 'true')
            }
        } catch (e) {
            console.error('Error accessing localStorage', e)
        }
    }, [isAuthenticated])

    useEffect(() => {
        if (user?.worker) {
            if (user?.worker?.mserp_employmentcategory === 'ANÜ' || user?.worker?.mserp_employmentcategory === 'FAK') {
                getOrders()
            }
        }
    }, [user])

    useEffect(() => {
        getOperationalPeriods()
    }, [user, accounts[0]])

    const scope: string = process.env.REACT_APP_SCOPE || ''

    const accessTokenRequest: SilentRequest = {
        scopes: [scope],
        account: accounts[0],
        forceRefresh: false,
    }

    const getCustomSnackbar = () => {
        dispatch(setAlert(null))
    }

    const getOrders = async () => {
        try {
            const date = format(new Date(), 'yyyy-MM-dd')
            const orders: IOrdersRef[] = await store.dispatch(injectedEndpoints.endpoints.getOrders.initiate(date, { forceRefetch: false })).unwrap()
            store.dispatch(setOrders(orders))
        } catch (error) {
            console.log(error)
        }
    }

    const getUser = async () => {
        try {
            await refreshUser()
        } finally {
            setUserLoaded(true)
        }
    }

    const getOperationalPeriods = async () => {
        getAuthConfig(instance, accounts[0])
            .then((headers: any) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    data: {},
                    params: {
                        date: new Date().toISOString(),
                    },
                }
                axios
                    .get(`${process.env.REACT_APP_BACKEND_URL}/OperationalPeriods`, config)
                    .then((response) => {
                        dispatch(setOperationalPeriods(response.data))
                    })
                    .catch((error) => {
                        console.log(error)
                    })
            })
            .catch((error: any) => {
                if (error instanceof InteractionRequiredAuthError) {
                    return instance.acquireTokenRedirect(accessTokenRequest)
                }
                if ((error as any)?.errorCode !== 'timed_out') {
                    console.log(error)
                }
            })
    }

    // const theme = React.useMemo(() => (prefersDarkMode ? DarkTheme : LightTheme), [prefersDarkMode])
    const theme = useSelector((state: any) => state.theme?.value)
    const effectiveTheme = theme === 'system' ? (prefersDarkMode ? 'dark' : 'light') : theme

    const operationalPeriods = useSelector((state: any) => state.operationalPeriods?.value)
    const maintenance = operationalPeriods.map((period: any) => period.Type === 1).includes(true) || (process.env.REACT_APP_MAINTENANCE ? process.env.REACT_APP_MAINTENANCE === 'true' : false)

    return isAuthenticated ? (
        <ThemeProvider theme={effectiveTheme === 'light' ? LightTheme : DarkTheme}>
            <CssBaseline />
            {!maintenance ? (
                <>
                    <Navigation onOpenQuickstart={() => setQuickstartOpen(true)} />
                    <QuickstartSidebar open={quickstartOpen} onClose={() => setQuickstartOpen(false)} />
                    <CustomSnackbar />
                    {!userLoaded || user.worker !== null || user.resource !== null ? (
                        <>
                            <Dashboard />
                            <Footer />
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', textWrap: 'balance', p: { xs: 4, md: 5, lg: 6 }, alignItems: { xs: 'center', md: 'start' }, textAlign: { xs: 'center', md: 'start' }, gap: 2 }}>
                            <Typography variant="h3" color="error">
                                {t('main.requestError')}
                            </Typography>
                            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
                                <Typography>
                                    <Trans i18nKey="main.userError" values={{ username: user.userid }} components={{ italic: <i /> }} />
                                </Typography>
                                <LaunchIcon color="inherit" fontSize="small" />
                                <Link
                                    href="https://mbti-serviceportal.app.corpintra.net/wm/app-SelfServicePortalTSS/landing-page/0da8c041-fbc7-ea11-4e91-005056b1ba68?view-options=%7B%22dialogId%22:%222db6f3ef-9b9b-eb11-9181-005056b1b743%22,%22type%22:%22SVMChangeRequestType%22,%22viewType%22:%22new%22%7D&presetParams=%7B%22SPSActivityClassBase%22:%7B%22CUSTSSPType%22:%22110%22,%20%22CUST_FOTicketType%22:%20%2210%22%7D%7D"
                                    target="_blank"
                                    rel="noopener"
                                    underline="hover"
                                >
                                    Service Portal
                                </Link>
                            </Stack>
                            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
                                <Typography>{t('navigation.support.serviceportalInfo')}</Typography>
                                <MailIcon color="inherit" fontSize="small" />
                                <Link href={`mailto:mbti-tickets.pool-id@mercedes-benz.com?subject=Request FO Timetracking - User ${user.userid} not found&cc=mbti-navteam@mercedes-benz.com`} target="_blank" rel="noopener" underline="hover">
                                    Support Mail
                                </Link>
                            </Stack>
                        </Box>
                    )}
                </>
            ) : (
                <Maintenance />
            )}
        </ThemeProvider>
    ) : (
        <NotAuthenticated />
    )
}

export default App
